import { Check } from "lucide-react";
import { useState, useEffect } from "react";
import Confetti from 'react-confetti';
import { Modal } from "./Modal";
import { doc, getDoc, setDoc } from 'firebase/firestore';

export const Pricing = ({ isLight, db, auth, appId }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [modalSubscription, setModalSubscription] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [recycleConfetti, setRecycleConfetti] = useState(true);
  const [modalText, setModalText] = useState("");

  const hasSubscriptionChanged = isSubscribed !== modalSubscription;

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!auth.currentUser) {
        setIsLoading(false);
        return;
      }
      
      const user = auth.currentUser;
      const userDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid);

      try {
        const userSnapshot = await getDoc(userDocRef);
        if (userSnapshot.exists()) {
          const userData = userSnapshot.data();
          const subscribed = userData.tier?.mailingList === 1;
          setIsSubscribed(subscribed);
          setModalSubscription(subscribed);
        }
      } catch (error) {
        console.error("Error fetching user subscription status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, [auth, db, appId]);

  const handlePremiumClick = () => {
    if (isLoading || !auth.currentUser) return;
    setModalSubscription(isSubscribed);
    if (isSubscribed) {
      setModalText("You are currently subscribed to our mailing list. You can unsubscribe at any time.");
    } else {
      setModalText("Our Premium tier is currently in development and will be launching soon. Join our mailing list to be the first to know when it's available!");
    }
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSave = async () => {
    if (!auth.currentUser) return;

    const user = auth.currentUser;
    const userDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'users', user.uid);

    try {
      await setDoc(userDocRef, { 
        tier: { 
          mailingList: modalSubscription ? 1 : 0
        } 
      }, { merge: true });

      setIsSubscribed(modalSubscription);
      if (modalSubscription) {
        setShowConfetti(true);
        setRecycleConfetti(true);
        setTimeout(() => setRecycleConfetti(false), 2000);
        setTimeout(() => setShowConfetti(false), 7000);
      }

    } catch (error) {
      console.error("Error saving subscription status:", error);
    } finally {
      setModalOpen(false);
    }
  };

  const tiers = [
    {
      name: "Free",
      price: "$0",
      description: "Essential defense for casual browsing.",
      features: [
        "10 searches per day",
        "Basic Verdict (Safe/Fraud)",
        "Neural Reasoning text",
        "Standard Response Time"
      ],
      cta: "Free Tier",
      highlight: false,
      action: () => {}
    },
    {
      name: "Premium",
      price: "$2",
      description: "High-tier neural access for power users.",
      features: [
        "100 searches per day",
        "Detailed Threat Intelligence",
        "30-day History Storage",
        "Deep Website Metadata",
        "Priority AI Processing"
      ],
      cta: "Premium Tier",
      highlight: true,
      action: handlePremiumClick
    }
  ];

  return (
    <>
      {showConfetti && <Confetti recycle={recycleConfetti} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in px-2">
        {tiers.map((tier, idx) => (
          <div 
            key={idx}
            onClick={tier.action}
            className={`group relative rounded-2xl p-8 border transition-all duration-500 flex flex-col ${
              tier.highlight 
                ? (isLight 
                    ? 'bg-slate-900 text-white border-slate-900 hover:shadow-2xl hover:-translate-y-2 cursor-pointer' 
                    : 'bg-white text-black hover:shadow-[0_0_50px_rgba(255,255,255,0.15)] hover:-translate-y-2 cursor-pointer') 
                : (isLight 
                    ? 'bg-white border-slate-200 text-slate-900 hover:border-slate-400 hover:shadow-lg hover:-translate-y-1 cursor-default' 
                    : 'bg-[#0d0d0f] border-white/10 text-white hover:border-white/30 hover:bg-[#121214] hover:-translate-y-1 cursor-default')
            }`}
          >
            {tier.highlight && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-[8px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-3xl z-20 shadow-lg">
                Recommended
              </div>
            )}
            
            <div className="mb-8">
              <h3 className={`text-[10px] font-black uppercase tracking-[0.4em] mb-2 transition-opacity group-hover:opacity-100 ${tier.highlight ? 'opacity-80' : 'opacity-40'}`}>
                {tier.name}
              </h3>
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-black italic tracking-tighter transition-transform group-hover:scale-105 origin-left duration-500">
                  {tier.price}
                </span>
                <span className="text-[10px] font-bold opacity-40">/mo</span>
              </div>
            </div>

            <p className="text-[11px] leading-relaxed mb-8 opacity-60 min-h-[40px]">
              {tier.description}
            </p>

            <ul className="space-y-4 mb-10 flex-grow">
              {tier.features.map((feature, fIdx) => (
                <li key={fIdx} className="flex items-center gap-3 group/item">
                  <div className={`p-1.5 rounded-3xl transition-transform duration-300 group-hover/item:scale-125 ${
                    tier.highlight ? 'bg-cyan-500/20 text-cyan-500' : 'bg-cyan-500/10 text-cyan-500'
                  }`}>
                    <Check size={10} strokeWidth={3} />
                  </div>
                  <span className="text-[10px] font-bold tracking-wide transition-colors group-hover/item:text-cyan-500">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>

            <button className={`w-full py-5 rounded-lg text-[10px] font-black uppercase tracking-[0.4em] transition-all duration-300 active:scale-95 flex items-center justify-center gap-2 group/btn ${
              tier.highlight
                ? (isLight ? 'bg-white text-black hover:bg-slate-100 shadow-xl' : 'bg-black text-white hover:bg-neutral-800 shadow-xl')
                : (isLight ? 'bg-slate-100 hover:bg-slate-200 text-slate-900' : 'bg-white/5 hover:bg-white/10 text-white')
            }`}>
              <span>{tier.cta}</span>
              
            </button>
          </div>
        ))}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={handleModalClose} 
        title="Coming Soon!"
        isLight={isLight}
      >
        <p className="text-sm mb-6">
          {modalText}
        </p>
        <div className="flex items-center space-x-3 p-4 rounded-lg bg-slate-500/10">
          <input
            type="checkbox"
            id="subscribe"
            checked={modalSubscription}
            onChange={(e) => setModalSubscription(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
          />
          <label htmlFor="subscribe" className="text-sm font-medium">
            Notify me when Premium is released
          </label>
        </div>
        <button 
          onClick={hasSubscriptionChanged ? handleModalSave : handleModalClose}
          className={`w-full mt-6 font-bold py-3 rounded-lg text-xs transition-colors ${isLight ? 'bg-slate-900 text-white hover:bg-slate-800' : 'bg-white text-black hover:bg-slate-200'}`}
        >
          {hasSubscriptionChanged ? 'Save' : 'Close'}
        </button>
      </Modal>
    </>
  );
};
